import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { HardHat, Eye, EyeOff, Loader2, Download, ClipboardCheck, LayoutDashboard, CalendarDays, BookOpen, GraduationCap, FolderKanban, Users, BarChart3, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ForgotPasswordDialog } from '@/components/ForgotPasswordDialog';
import { WhatsAppContactPopover } from '@/components/WhatsAppContactPopover';

const STORAGE_KEY = 'gerobra_saved_credentials';

export default function Login() {
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Load saved email on mount (password is NOT stored for security)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { email: savedEmail } = JSON.parse(saved);
        if (savedEmail) setEmail(savedEmail);
        setRememberMe(true);
      }
    } catch (e) {
      console.error('Failed to load saved email:', e);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao entrar',
        description: error.message || 'Email ou senha incorretos. Tente novamente.',
      });
    } else {
      // Save or clear email based on rememberMe (password is NOT stored for security)
      if (rememberMe) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ email }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
      
      toast({
        title: 'Bem-vindo!',
        description: 'Login realizado com sucesso.',
      });
      navigate('/home');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <Helmet>
        <title>Entrar — GEROBRA</title>
        <meta name="description" content="Acesse sua conta GEROBRA: plataforma de gestão de obras com inspeções digitais, cronograma, estoque e mão de obra." />
        <link rel="canonical" href="https://gerobra.ia.br/login" />
        <meta property="og:title" content="Entrar — GEROBRA" />
        <meta property="og:description" content="Acesso à plataforma GEROBRA de gestão de obras." />
        <meta property="og:url" content="https://gerobra.ia.br/login" />
      </Helmet>
      {/* Left side - Branding with warm tones */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(165deg, #8B5A2B 0%, #A0522D 50%, #CD853F 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-amber-200 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-200 rounded-full blur-3xl" />
        </div>
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(255,241,206,0.15) 1px, transparent 1px), 
                             linear-gradient(90deg, rgba(255,241,206,0.15) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}
        />
        
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-12">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/25 shadow-xl">
              <HardHat className="h-9 w-9 text-amber-100" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">GEROBRA</h1>
              <p className="text-amber-100/80 text-sm font-medium">Gestão da Qualidade</p>
            </div>
          </div>

          {/* Tagline */}
          <div className="space-y-6 max-w-lg">
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight">
              Gestão de obras
              <span className="block text-amber-100/90">completa e inteligente</span>
            </h2>
            <p className="text-lg text-amber-100/70 leading-relaxed">
              Plataforma integrada para qualidade, planejamento, equipes e documentação na construção civil.
            </p>
          </div>

          {/* Features list */}
          <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-3">
            {[
              { icon: ClipboardCheck, label: 'Checklists e Inspeções' },
              { icon: LayoutDashboard, label: 'Dashboard executivo' },
              { icon: HardHat, label: 'Gestão de mão de obra' },
              { icon: GraduationCap, label: 'Treinamentos' },
              { icon: CalendarDays, label: 'Cronograma de obras' },
              { icon: BookOpen, label: 'Diário de obra' },
              { icon: FolderKanban, label: 'Projetos e documentos' },
              { icon: BarChart3, label: 'Relatórios e métricas' },
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-2.5 text-amber-100/90">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100/20">
                  <feature.icon className="h-3 w-3 text-amber-100" />
                </div>
                <span className="text-sm font-medium">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <main className="flex-1 flex items-center justify-center p-6 lg:p-12" style={{ background: 'linear-gradient(180deg, #fff1ce 0%, #ffe4b5 100%)' }}>
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile logo */}
          <div className="text-center mb-8 lg:hidden">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #8B5A2B 0%, #A0522D 100%)' }}>
              <HardHat className="h-9 w-9 text-amber-100" />
            </div>
            <h1 className="text-3xl font-bold text-amber-900">GEROBRA</h1>
            <p className="text-amber-700/80 mt-1">Gestão da Qualidade na Construção</p>
            
            {/* Install App Button - Mobile only */}
            <Link 
              to="/install"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 text-sm font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-full transition-colors"
            >
              <Download className="h-4 w-4" />
              Instalar App
            </Link>
          </div>

          <Card className="shadow-xl border border-amber-200/50 bg-white/90 backdrop-blur-sm">
            <CardHeader className="text-center pb-2 pt-8">
              <CardTitle className="text-2xl font-bold text-amber-900">Bem-vindo de volta</CardTitle>
              <CardDescription className="text-base text-amber-700/80">
                Digite suas credenciais para acessar
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-sm font-medium text-amber-900">
                    Email
                  </Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="h-12 rounded-xl border-2 border-amber-200 bg-white px-4 text-base transition-all duration-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 placeholder:text-amber-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-sm font-medium text-amber-900">
                    Senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="h-12 rounded-xl border-2 border-amber-200 bg-white px-4 pr-12 text-base transition-all duration-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 placeholder:text-amber-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-500 hover:text-amber-700 transition-colors p-1"
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember-me"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                      className="border-amber-300 data-[state=checked]:bg-amber-700 data-[state=checked]:border-amber-700"
                    />
                    <Label 
                      htmlFor="remember-me" 
                      className="text-sm text-amber-700 cursor-pointer select-none"
                    >
                      Lembrar-me
                    </Label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForgotPasswordOpen(true)}
                    className="text-sm text-amber-700 font-medium hover:text-amber-900 hover:underline transition-colors"
                  >
                    Esqueceu sua senha?
                  </button>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 text-white" 
                  style={{ background: 'linear-gradient(135deg, #8B5A2B 0%, #A0522D 100%)' }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    'Entrar'
                  )}
                </Button>

                <div className="pt-2">
                  <WhatsAppContactPopover
                    align="center"
                    trigger={
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12 rounded-xl font-semibold text-base border-2 border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800"
                      >
                        <MessageCircle className="mr-2 h-5 w-5" />
                        Solicitar acesso
                      </Button>
                    }
                  />
                  <p className="text-center text-xs text-amber-700/70 mt-2">
                    Não tem cadastro? Fale com o administrador.
                  </p>
                </div>
              </form>

              <ForgotPasswordDialog 
                open={forgotPasswordOpen} 
                onOpenChange={setForgotPasswordOpen} 
              />
            </CardContent>
          </Card>

          <div className="text-center mt-8 space-y-2">
            <p className="text-sm text-amber-700/70">
              Sistema de gestão para construção civil.
            </p>
            <p className="text-xs text-amber-600/60">
              Ao entrar, você concorda com nossos{' '}
              <Link 
                to="/terms" 
                className="underline hover:text-amber-800 transition-colors"
              >
                Termos de Uso
              </Link>
              {' '}e{' '}
              <Link 
                to="/privacy" 
                className="underline hover:text-amber-800 transition-colors"
              >
                Política de Privacidade
              </Link>
              .
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}